import { useState, type ReactNode } from 'react'
import { COLORS } from '../lib/theme'

// Surface-style icon button per design system (Natlink DS, node 211:4908).
// Circular surface chip with a drop shadow and hover/press/active state layer.
// Shared by the map editor controls and the plan-hunt drive editor map.
export function SurfaceIconButton({
  children,
  title,
  onClick,
  disabled,
  active,
}: {
  children: ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const outerStyle: React.CSSProperties = {
    width: 36,
    height: 36,
    padding: 0,
    border: "none",
    background: "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: disabled ? "default" : "pointer",
    filter: "drop-shadow(0px 4px 2px rgba(230, 238, 232, 0.25))",
  };

  const showStateLayer = !disabled && (pressed || active || hovered);
  const stateLayerBg = pressed || active
    ? "rgba(26, 29, 28, 0.12)"
    : hovered
      ? "rgba(26, 29, 28, 0.08)"
      : "transparent";

  const containerStyle: React.CSSProperties = {
    width: disabled ? "100%" : 36,
    height: disabled ? "100%" : 36,
    borderRadius: 9999,
    background: disabled ? "rgba(26, 29, 28, 0.12)" : COLORS.surface,
    color: COLORS.onSurface,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    opacity: disabled ? 0.38 : 1,
  };

  const stateLayerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: showStateLayer ? stateLayerBg : "transparent",
  };

  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={outerStyle}
    >
      <span style={containerStyle}>
        <span style={stateLayerStyle}>{children}</span>
      </span>
    </button>
  );
}
